import React from "react";
import { Star, ExternalLink } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Google-style reviews widget. Static content (early testers + trainer feedback)
 * with an aggregate score and a CTA link to the Google Business profile
 * (env: REACT_APP_GOOGLE_REVIEWS_URL, falls back to a Google search).
 */
const REVIEWS_URL =
  process.env.REACT_APP_GOOGLE_REVIEWS_URL ||
  "https://search.google.com/local/writereview?placeid=ChIJW22CWWfImkcRuQrZRYrtjxs";

const Stars = ({ n, className = "w-3.5 h-3.5" }) => (
  <div className="flex items-center gap-0.5" aria-label={`${n} of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={`star-${i}`}
        className={`${className} ${
          i < n ? "fill-[#FBBC04] text-[#FBBC04]" : "text-white/20"
        }`}
      />
    ))}
  </div>
);

const ReviewsSection = () => {
  const { lang } = useLanguage();
  const isDe = lang === "de";

  const t = {
    kicker: isDe ? "Vertrauen · Google Bewertungen" : "Trust · Google Reviews",
    title: isDe ? "Was Kunden sagen." : "What customers say.",
    subtitle: isDe
      ? "Ehrliche Bewertungen von echten 4keeperz-Kunden auf Google."
      : "Honest reviews from real 4keeperz customers on Google.",
    verified: isDe ? "Verifizierter Google-Kunde" : "Verified Google customer",
    write_review: isDe ? "Google Bewertung schreiben" : "Write a Google review",
    based_on: isDe ? "basierend auf {n} Bewertungen" : "based on {n} reviews",
    reviews: [
      {
        name: "Adrian Bitic",
        role: isDe ? "Vor 6 Stunden · Google" : "6 hours ago · Google",
        text: isDe
          ? "Ich habe mir die Torwarthandschuhe von 4keeperz bestellt und bin wirklich sehr zufrieden. Die Qualität und der Grip sind…"
          : "I ordered the goalkeeper gloves from 4keeperz and am really very satisfied. The quality and grip are…",
        stars: 5,
      },
      {
        name: "Samuel Brown",
        role: isDe ? "Vor 5 Tagen · Google" : "5 days ago · Google",
        text: isDe
          ? "Schneller Lieferservice und sehr gute Qualität von den Handschuhen!"
          : "Fast delivery and very good quality of the gloves!",
        stars: 5,
      },
      {
        name: "Louis Bunkowski",
        role: isDe ? "Vor 1 Woche · Google" : "1 week ago · Google",
        text: isDe
          ? "Sehr schnelle Lieferung und qualitativ wirklich sehr hochwertige Handschuhe. Definitiv kaufe ich sie mir wieder 🔥"
          : "Very fast delivery and truly high-quality gloves. Will definitely buy again 🔥",
        stars: 5,
      },
    ],
    // 2 additional 5★ reviews without text (Jeton Ibrahimi, Daniel Dominguez)
    extra_star_only_count: 2,
    star_only_label: isDe
      ? "Weitere 5-Sterne-Bewertungen ohne Kommentar"
      : "More 5-star ratings without comment",
    total_reviews: 5,
  };

  const avg = (
    t.reviews.reduce((s, r) => s + r.stars, 0) / t.reviews.length
  ).toFixed(1);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-20 md:py-28 bg-black border-b border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#00A651]/10 border border-[#00A651]/40 text-[#00A651] font-condensed uppercase tracking-widest text-[10px] px-2.5 py-1 mb-4">
              {t.kicker}
            </div>
            <h2 className="font-brush text-5xl md:text-6xl leading-none text-white">
              {t.title}
            </h2>
            <p className="text-white/60 mt-3 max-w-xl">{t.subtitle}</p>
          </div>

          {/* Aggregate widget */}
          <div
            data-testid="reviews-aggregate"
            className="border border-white/10 bg-[#0A0A0A] p-5 flex items-center gap-4 shrink-0"
          >
            {/* Google G */}
            <div className="w-11 h-11 border border-white/10 flex items-center justify-center bg-white">
              <svg viewBox="0 0 48 48" className="w-6 h-6" aria-hidden="true">
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19A23.94 23.94 0 000 24c0 3.87.93 7.53 2.56 10.78l7.97-6.19z"
                />
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-display text-3xl text-white leading-none">
                  {avg}
                </span>
                <Stars n={5} className="w-4 h-4" />
              </div>
              <div className="text-[11px] text-white/50 font-condensed uppercase tracking-widest mt-1">
                Google · {t.based_on.replace("{n}", t.total_reviews)}
              </div>
            </div>
          </div>
        </div>

        {/* Review cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {t.reviews.map((r, i) => (
            <article
              key={`review-${i}`}
              data-testid={`review-card-${i}`}
              className="border border-white/10 bg-gradient-to-br from-[#0A0A0A] to-black p-6 flex flex-col gap-3 hover:border-[#00A651]/40 transition-colors"
            >
              <Stars n={r.stars} />
              <p className="text-white/80 text-sm leading-relaxed flex-1">
                „{r.text}“
              </p>
              <div className="pt-3 border-t border-white/5">
                <div className="font-display text-base text-white leading-tight">
                  {r.name}
                </div>
                <div className="text-[11px] text-white/50 font-condensed uppercase tracking-widest mt-0.5">
                  {r.role}
                </div>
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#00A651] font-condensed uppercase tracking-widest">
                  <span className="inline-block w-1.5 h-1.5 bg-[#00A651] rounded-full" />
                  {t.verified}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* CTA + star-only-count note */}
        <div className="mt-10 flex flex-col items-center gap-3">
          {t.extra_star_only_count > 0 && (
            <div
              data-testid="reviews-star-only-note"
              className="text-[11px] text-white/40 font-condensed uppercase tracking-widest flex items-center gap-2"
            >
              <span className="inline-flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={`sonote-${i}`} className="w-3 h-3 fill-[#FBBC04] text-[#FBBC04]" />
                ))}
              </span>
              +{t.extra_star_only_count} {t.star_only_label}
            </div>
          )}
          <a
            href={REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer nofollow"
            data-testid="reviews-write-cta"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-[#00A651] text-white/80 hover:text-[#00A651] font-condensed uppercase tracking-widest text-xs px-5 py-3 transition-colors"
          >
            {t.write_review}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
